import { EDITOR_DATA_LIST_TYPE, isBlockLevelContent } from '../dom/content';
import { isListItemElement, isOrderedListElement } from '../dom/list';
import { getSelectedCellsPlan, getTableFromTablePlan } from '../dom/table';
import { htmlToText } from '../htmlToText';
import { everyInIterable } from '../iterable';
import { findLinkAncestor } from '../link';
import { markdownFromDom } from '../markdown/markdownFromDom';
import { isSelectionContainedByNode, isSelectionPresent } from '../selection';
import { removeSelectionBookmarksInElement } from '../selectionBookmarks';
import { getStyleFromRange } from '../style/getStyleFromRange';
import { serializeStyle } from '../style/style';

export interface HtmlEditorClipping {
  html: string;
  markdown?: string;
  text: string;
}

export interface ClipSelectionOptions {
  hasTableService?: boolean;
  shouldReturnMarkdown?: boolean;
}

export function clipSelection(
  container: Element,
  selection: Selection | null,
  options: ClipSelectionOptions = {},
): HtmlEditorClipping | undefined {
  const document = container.ownerDocument;
  if (!document || !isSelectionPresent(selection) || !isSelectionContainedByNode(container, selection)) {
    return undefined;
  }

  const range = selection.getRangeAt(0);
  if (range.collapsed) {
    return undefined;
  }

  // Are we copying a table cells only?
  if (options.hasTableService) {
    const selectedTableCells = getSelectedCellsPlan(container, range); // Will return null if selection contains more than table cells.
    if (selectedTableCells !== null) {
      // If so put a table equivalent to selected cells in clipboard.
      const table = getTableFromTablePlan(selectedTableCells);
      if (table !== null) {
        removeSelectionBookmarksInElement(table);
        return {
          html: table.outerHTML,
          text: table.innerText,
        };
      }
    }
  }

  const { commonAncestorContainer } = range;
  const fragment = range.cloneContents();

  // Are we copying text / phrasing content only?
  if (everyInIterable(fragment.childNodes, childNode => !isBlockLevelContent(childNode))) {
    let wrapper: Element | null = null;

    // Keep links.
    const linkAncestor = findLinkAncestor(commonAncestorContainer, container);
    if (linkAncestor) {
      wrapper = linkAncestor.cloneNode(false) as typeof linkAncestor;
    }

    // Keep (customized) styles.
    const style = getStyleFromRange(container, range);
    const styleCss = serializeStyle(style);
    if (styleCss) {
      wrapper = wrapper || document.createElement('span');
      wrapper.setAttribute('style', styleCss);
    }

    if (wrapper) {
      wrapper.appendChild(fragment);
      fragment.appendChild(wrapper);
    }
  }

  // If we're copying some ordered list elements, we'll want them back in a ordered list when pasting.
  if (isOrderedListElement(commonAncestorContainer)) {
    fragment.childNodes.forEach(node => {
      if (isListItemElement(node)) {
        node.setAttribute(EDITOR_DATA_LIST_TYPE, 'ol');
      }
    });
  }

  const text = htmlToText(fragment);
  const markdown = options.shouldReturnMarkdown ? markdownFromDom(fragment) : undefined;

  const element = document.createElement('div');
  element.appendChild(fragment);
  removeSelectionBookmarksInElement(element);
  const html = element.innerHTML;

  return {
    html,
    markdown,
    text,
  };
}
